import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { zodSupplierLetterMetaSchema } from '../validations';

import {
  type SupplierLetterMeta,
  type UploadSupplierLetterRequest,
} from './types';
import { zodUploadSupplierLetterRequestSchema } from './validations';

/**
 * Uploads or replaces the supplier authorization letter for a CompanyAccount.
 *
 * @param companyAccountId - The ID of the company account.
 * @param body - The file metadata for the supplier letter.
 * @returns The stored supplier letter metadata.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/put_company_account_supplier_letter_vadmin_company_accounts__company_account_id__supplier_letter_put
 */
// fallow-ignore-next-line complexity
export const putCompanyAccountSupplierLetter = async (
  companyAccountId: UUID,
  body: UploadSupplierLetterRequest,
) => {
  try {
    return await vestigasApi
      .put(ENDPOINT.COMPANY.ACCOUNT.SUPPLIER_LETTER.PUT(companyAccountId), {
        json: body,
        validate: {
          body: zodUploadSupplierLetterRequestSchema,
          // Response carries a time-limited SAS download URL; keep it out of
          // Sentry breadcrumbs/logs.
          isResponseSensitive: true,
          response: zodSupplierLetterMetaSchema,
        },
      })
      .json<SupplierLetterMeta>();
  } catch (error) {
    log.error({
      message: 'Error uploading company account supplier letter',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.putCompanyAccountSupplierLetter.error422,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) =>
              $.companyAccount.putCompanyAccountSupplierLetter.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
